import { useEffect, useState, type ComponentProps, type ComponentType } from "react";
import { GraduationCap, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SessionProvider, useSession } from "@/state/session";
import { I18nProvider, useI18n } from "@/i18n";
import { AppMenu } from "@/components/app-menu";
import { AnnouncementBar } from "@/components/announcement-bar";
import { OfflineBanner, AutoUpdate } from "@/components/pwa";
import { CookieBanner } from "@/components/cookie-banner";
import { Link } from "@/components/link";
import { currentPath, match, navigate, useRoute } from "@/lib/router";
import { usePageViews } from "@/lib/analytics";
import { ROUTES as ROUTE_DEFS, type RoutePattern } from "@/routes";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";

import { EmailLinkGate, SignInPage } from "@/features/auth/SignInPage";


/**
 * Every page is its own chunk: a visitor on `/telc` should not download the exam, the oral
 * runner and `/admin` before the prerendered page can come alive.
 *
 * Not `React.lazy` + `Suspense`. The prerendered HTML is a snapshot of the client's own
 * render, so it carries none of the markers React's hydration looks for at a `Suspense`
 * boundary: one on the path made hydration give up, clear the page and show a spinner.
 * Here the component is held in a variable. `main.tsx` awaits `preload` for the page the
 * address names before it hydrates, so the first render finds it in hand and produces the
 * very markup that is already on screen; a page reached later shows the spinner for as long
 * as its chunk takes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageComponent<C extends ComponentType<any>> = ComponentType<ComponentProps<C>> & {
  preload: () => Promise<unknown>;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function page<C extends ComponentType<any>>(load: () => Promise<{ default: C }>): PageComponent<C> {
  let loaded: C | null = null;
  let loading: Promise<unknown> | null = null;
  const preload = (): Promise<unknown> =>
    (loading ??= load().then((mod) => {
      loaded = mod.default;
    }));
  const Page = ((props: ComponentProps<C>) => {
    const [Component, setComponent] = useState<C | null>(() => loaded);
    useEffect(() => {
      if (Component) return;
      let live = true;
      void preload().then(() => live && setComponent(() => loaded));
      return () => {
        live = false;
      };
    }, [Component]);
    if (!Component) return <PageFallback />;
    return <Component {...props} />;
  }) as PageComponent<C>;
  Page.preload = preload;
  return Page;
}

const LandingPage = page(() => import("@/features/landing/LandingPage").then((m) => ({ default: m.LandingPage })));
const TelcExamPage = page(() => import("@/features/landing/TelcExamPage").then((m) => ({ default: m.TelcExamPage })));
const UseCasePage = page(() => import("@/features/landing/UseCasePage").then((m) => ({ default: m.UseCasePage })));
const CertyfikatPage = page(() => import("@/features/landing/CertyfikatPage").then((m) => ({ default: m.CertyfikatPage })));
const ComparePage = page(() => import("@/features/landing/ComparePage").then((m) => ({ default: m.ComparePage })));
const PricingPage = page(() => import("@/features/landing/PricingPage").then((m) => ({ default: m.PricingPage })));
const AccountPage = page(() => import("@/features/auth/AccountPage").then((m) => ({ default: m.AccountPage })));
const PassPage = page(() => import("@/features/billing/PassPage").then((m) => ({ default: m.PassPage })));
const DashboardPage = page(() => import("@/features/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const SetPage = page(() => import("@/features/dashboard/SetPage").then((m) => ({ default: m.SetPage })));
const ExamPage = page(() => import("@/features/exam/ExamPage").then((m) => ({ default: m.ExamPage })));
const ResultPage = page(() => import("@/features/exam/ResultPage").then((m) => ({ default: m.ResultPage })));
const ReviewPage = page(() => import("@/features/exam/ReviewPage").then((m) => ({ default: m.ReviewPage })));
const PracticePage = page(() => import("@/features/practice/PracticePage").then((m) => ({ default: m.PracticePage })));
const SpeakingPage = page(() => import("@/features/speaking/SpeakingPage").then((m) => ({ default: m.SpeakingPage })));
const OralExamPage = page(() => import("@/features/speaking/OralExamPage").then((m) => ({ default: m.OralExamPage })));
const OralResultPage = page(() => import("@/features/speaking/OralResultPage").then((m) => ({ default: m.OralResultPage })));
const RulesPage = page(() => import("@/features/rules/RulesPage").then((m) => ({ default: m.RulesPage })));
const WritingHistoryPage = page(() => import("@/features/history/WritingHistoryPage").then((m) => ({ default: m.WritingHistoryPage })));
const AdminPage = page(() => import("@/features/admin/AdminPage").then((m) => ({ default: m.AdminPage })));
const CookiePolicyPage = page(() => import("@/features/legal/LegalPages").then((m) => ({ default: m.CookiePolicyPage })));
const PrivacyPage = page(() => import("@/features/legal/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = page(() => import("@/features/legal/LegalPages").then((m) => ({ default: m.TermsPage })));
const ContactPage = page(() => import("@/features/legal/ContactPage").then((m) => ({ default: m.ContactPage })));
const CyclePage = page(() => import("@/features/landing/CyclePage").then((m) => ({ default: m.CyclePage })));
const MaterialsPage = page(() => import("@/features/landing/MaterialsPage").then((m) => ({ default: m.MaterialsPage })));
const SchoolsPage = page(() => import("@/features/landing/SchoolsPage").then((m) => ({ default: m.SchoolsPage })));
const TutorsPage = page(() => import("@/features/landing/TutorsPage").then((m) => ({ default: m.TutorsPage })));
const VocabularyPage = page(() => import("@/features/landing/VocabularyPage").then((m) => ({ default: m.VocabularyPage })));
const CertyfikatLevelPage = page(() => import("@/features/landing/CertyfikatLevelPage").then((m) => ({ default: m.CertyfikatLevelPage })));
const WritingGuidePage = page(() => import("@/features/landing/WritingGuidePage").then((m) => ({ default: m.WritingGuidePage })));
const TelcTasksPage = page(() => import("@/features/landing/TelcTasksPage").then((m) => ({ default: m.TelcTasksPage })));
const StateTasksPage = page(() => import("@/features/landing/StateTasksPage").then((m) => ({ default: m.StateTasksPage })));
const TelcPartGuidePage = page(() => import("@/features/landing/TelcPartGuidePage").then((m) => ({ default: m.TelcPartGuidePage })));
const CertyfikatGuidePage = page(() => import("@/features/landing/CertyfikatGuidePage").then((m) => ({ default: m.CertyfikatGuidePage })));
const BlogIndexPage = page(() => import("@/features/blog/BlogIndexPage").then((m) => ({ default: m.BlogIndexPage })));
const BlogPostPage = page(() => import("@/features/blog/BlogPostPage").then((m) => ({ default: m.BlogPostPage })));

/** The component for each pattern in `@/routes`. Keys must stay in step with it —
 *  TypeScript enforces that: `RoutePattern` is the union of the patterns there. */
const RENDER: Record<RoutePattern, (params: Record<string, string>) => React.ReactNode> = {
  "/": () => <LandingPage />,
  "/telc": () => <TelcExamPage />,
  "/karta-pobytu": () => <UseCasePage useCase="residence" />,
  "/studia": () => <UseCasePage useCase="studies" />,
  "/certyfikat": () => <CertyfikatPage />,
  "/porownanie": () => <ComparePage />,
  "/obywatelstwo": () => <UseCasePage useCase="citizenship" />,
  "/pricing": () => <PricingPage />,
  "/dla-szkol": () => <SchoolsPage />,
  "/dla-korepetytorow": () => <TutorsPage />,
  "/terminy": () => <CyclePage />,
  "/arkusze": () => <MaterialsPage />,
  "/slowka": () => <VocabularyPage />,
  "/poradnik": () => <BlogIndexPage />,
  "/poradnik/:slug": (p) => <BlogPostPage slug={p.slug!} />,
  "/telc/zasady": () => <RulesPage />,
  "/certyfikat/zasady": () => <RulesPage exam="certyfikat" />,
  "/certyfikat/b1": () => <CertyfikatLevelPage level="B1" />,
  "/certyfikat/b2": () => <CertyfikatLevelPage level="B2" />,
  "/certyfikat/zadania": () => <StateTasksPage />,
  "/certyfikat/pisanie": () => <CertyfikatGuidePage guide="writing" />,
  "/certyfikat/mowienie-b1": () => <CertyfikatGuidePage guide="speaking-b1" />,
  "/certyfikat/mowienie-b2": () => <CertyfikatGuidePage guide="speaking-b2" />,
  "/telc/zadania": () => <TelcTasksPage />,
  "/telc/list": () => <TelcPartGuidePage guide="letter" />,
  "/telc/opis-zdjecia": () => <TelcPartGuidePage guide="picture" />,
  "/telc/prezentacja": () => <TelcPartGuidePage guide="presentation" />,
  "/pisanie": () => <WritingGuidePage />,
  "/terms": () => <TermsPage />,
  "/privacy": () => <PrivacyPage />,
  "/cookies": () => <CookiePolicyPage />,
  "/kontakt": () => <ContactPage />,
  "/account": () => <AccountPage />,
  "/pass": () => <PassPage />,
  "/app": () => <DashboardPage />,
  "/set/:setId": (p) => <SetPage setId={p.setId!} />,
  "/set/:setId/speaking": (p) => <SpeakingPage setId={p.setId!} />,
  "/set/:setId/oral": (p) => <OralExamPage setId={p.setId!} />,
  "/oral/:sessionId": (p) => <OralResultPage sessionId={p.sessionId!} />,
  "/exam/:attemptId": (p) => <ExamPage attemptId={p.attemptId!} />,
  "/practice/:attemptId": (p) => <PracticePage attemptId={p.attemptId!} />,
  "/result/:attemptId": (p) => <ResultPage attemptId={p.attemptId!} />,
  "/review/:attemptId": (p) => <ReviewPage attemptId={p.attemptId!} />,
  "/writing": () => <WritingHistoryPage />,
  "/admin": () => <AdminPage />,
};

const ROUTES = ROUTE_DEFS.map((def) => ({
  pattern: def.pattern,
  public: "public" in def && def.public === true,
  render: RENDER[def.pattern],
}));

function PageFallback() {
  return (
    <div className="flex items-center gap-2 py-20 text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
    </div>
  );
}

/** Fetches the chunk of the page the address names. `main.tsx` awaits this before hydrating, so
 *  a prerendered page is never replaced by a spinner while its code arrives. */
export async function preloadCurrentPage(): Promise<void> {
  const path = currentPath();
  const hit = ROUTES.map((route) => ({ route, params: match(route.pattern, path) })).find(
    (candidate) => candidate.params !== null,
  );
  if (!hit) return;
  const element = hit.route.render(hit.params!) as { type?: { preload?: () => Promise<unknown> } };
  await element.type?.preload?.();
}

function Router() {
  const path = useRoute();
  const session = useSession();
  const { t } = useI18n();

  const hit = ROUTES.map((route) => ({ route, params: match(route.pattern, path) })).find(
    (candidate) => candidate.params !== null,
  );

  if (!hit) {
    return (
      <Alert>
        <AlertTitle>{t("common.notFoundTitle")}</AlertTitle>
        <AlertDescription>
          <Button variant="link" className="px-0" onClick={() => navigate("/")}>
            {t("nav.backHome")}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (session.status === "error") {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t("common.signInFailed")}</AlertTitle>
        <AlertDescription>{session.error}</AlertDescription>
      </Alert>
    );
  }

  const needsAccount = !hit.route.public;

  if (needsAccount && session.status === "loading") {
    return (
      <div className="flex items-center gap-2 py-20 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> {t("common.loadingSession")}
      </div>
    );
  }

  // The door, rendered in place rather than redirected to: the route is kept,
  // so signing in lands on the page that was asked for instead of the sets.
  if (needsAccount && session.status !== "ready") return <SignInPage />;

  return <>{hit.route.render(hit.params!)}</>;
}

/** The pages laid out wide, header to footer: the landing and the two exam pages. */
const WIDE_PATHS = ["/", "/telc", "/certyfikat", "/pricing"];

function Shell() {
  const { t } = useI18n();
  const path = useRoute();
  usePageViews();
  const isPublic = ROUTE_DEFS.some(
    (r) => "public" in r && r.public === true && match(r.pattern, path) !== null,
  );

  // Every public page shares one frame (header, footer, left edge), so the chrome
  // never moves between them; a page that is read rather than looked at keeps a
  // narrow column inside it, flush with the same left edge. The signed-in app
  // keeps its own narrow frame.
  const wide = WIDE_PATHS.includes(path);
  const column = isPublic ? "max-w-5xl" : "max-w-3xl";

  return (
    // `clip`, not `hidden`: the wash behind each marketing page is wider than the
    // column on purpose, and clipping it here stops it from scrolling the page
    // sideways without making this a scroll container (which would break the
    // sticky header).
    // The signed-in app is not translated by the browser: its own interface is
    // already in the reader's language and the exam text is Polish on purpose. An
    // auto-translator swaps text nodes under React and the next render throws
    // (insertBefore / NotFoundError in Clarity).
    <div className="min-h-dvh overflow-x-clip" translate={isPublic ? undefined : "no"}>
      {/* The header height is fixed (h-14) because the sticky exam-block
          header needs to know what it comes to rest beneath. */}
      <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur">
        <div
          className={`mx-auto flex h-14 ${column} items-center gap-1 px-4 sm:px-5`}
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          <Link to="/" className="flex items-center gap-2 text-sm font-extrabold tracking-tight">
            <GraduationCap className="size-5 shrink-0" />
            {t("app.name")}{" "}
            <span className="font-semibold text-muted-foreground">{t("app.level")}</span>
          </Link>

          {isPublic ? (
            <MarketingNav />
          ) : (
            <div className="ml-auto">
              <AppMenu />
            </div>
          )}
        </div>
        <OfflineBanner />
      </header>
      {!isPublic && <AnnouncementBar />}

      <main
        className={`mx-auto ${column} px-4 pt-6 sm:px-5 sm:pt-8`}
        style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}
      >
        <div className={isPublic && !wide ? "max-w-3xl" : undefined}>
          <EmailLinkGate>
            <Router />
          </EmailLinkGate>
        </div>
      </main>
      <MarketingFooter variant={isPublic ? "full" : "compact"} wide={isPublic} />
      <CookieBanner />
      <AutoUpdate />
    </div>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <SessionProvider>
        <Shell />
      </SessionProvider>
    </I18nProvider>
  );
}
